/**
 * Wraps any crypto-using app's UI with the identity-unlock flow. Instead of
 * each app reimplementing a switch on enrolment/privateKey, they wrap their
 * top-level render in `<IdentityGate>…</IdentityGate>` and trust Core to
 * present the right Master-Code-setup / invitation-pending / unlock screen
 * before the children get rendered.
 *
 * The decision tree mirrors what `VaultRoot` used to do inline inside the
 * Documentation app, but is now app-agnostic and lives in Core.
 */
import type { ReactNode } from 'react';
import { useVault } from '../lib/vault';
import { IdentityUnlockScreen } from './IdentityUnlockScreen';
import { MasterCodeSetupScreen } from './MasterCodeSetupScreen';
import { InvitationPendingScreen } from './InvitationPendingScreen';

export function IdentityGate({ children }: { children: ReactNode }) {
  const enrolment = useVault((s) => s.enrolment);
  const encryptedPrivateKey = useVault((s) => s.encryptedPrivateKey);
  const privateKey = useVault((s) => s.privateKey);
  const vaultKey = useVault((s) => s.vaultKey);

  // `enrolment` is server-derived; if it ever says `needs_keypair` while the
  // store already holds an `encryptedPrivateKey`, the response was malformed
  // (e.g. stale-deploy returning the legacy `apps[]` shape). Falling through
  // to the unlock path is safe; showing MasterCodeSetupScreen here would
  // invite the user to overwrite their real keypair with a new random one
  // and lose vault access.
  if (enrolment === 'needs_keypair' && !encryptedPrivateKey) return <MasterCodeSetupScreen />;
  if (enrolment === 'needs_invitation') return <InvitationPendingScreen />;
  if (!privateKey || !vaultKey) return <IdentityUnlockScreen />;
  return <>{children}</>;
}
